import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { NotIOsAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { CongratsScreen } from '@sorare/core/src/components/congratulations/CongratsScreen';
import { TokenImg } from '@sorare/core/src/components/congratulations/ui';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_MY_CARDS_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { congratsScreens, glossary } from '@sorare/core/src/lib/glossary';

const Body = styled.div`
  position: relative;
  isolation: isolate;
  min-height: 100%;
`;

const Content = styled.div`
  padding: var(--quadruple-unit) 0;
`;

type Card = {
  slug: string;
  rarityTyped: Rarity;
  pictureUrl?: string | null;
  anyPlayer: {
    slug: string;
    displayName: string;
  };
};

export type Props = {
  card?: Card;
  open: boolean;
  onClose: () => void;
  ftue: boolean;
};

export const RewardCardDialog = ({ card, open, onClose, ftue }: Props) => {
  const { up: upTablet } = useScreenSize('tablet');
  const { generateSportContextPath } = useSportContext();

  const continueBtn = (
    <Button size="medium" color="secondary" onClick={onClose}>
      <FormattedMessage {...glossary.continue} />
    </Button>
  );
  const viewMyGalleryBtn = (
    <Button
      size="medium"
      color={ftue ? 'secondary' : 'transparent'}
      to={generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS)}
      onClick={onClose}
    >
      <FormattedMessage {...glossary.viewGallery} />
    </Button>
  );

  if (!card) return null;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={onClose}
      hideHeader
      fullScreen={!upTablet}
    >
      <Body>
        <Content>
          <CongratsScreen
            rarityBackground={card.rarityTyped}
            title={
              <FormattedMessage
                {...congratsScreens.newSigning}
                values={{ br: <br />, cardsCount: 1 }}
              />
            }
            subtitle={
              <FormattedMessage
                {...congratsScreens.welcomePlayerToYourTeam}
                values={{
                  playerDisplayName: card.anyPlayer.displayName,
                }}
              />
            }
            main={
              <TokenImg>
                <CardImg src={card.pictureUrl} width={320} />
              </TokenImg>
            }
            actions={
              <>
                {continueBtn}
                <NotIOsAppFeature>{viewMyGalleryBtn}</NotIOsAppFeature>
              </>
            }
            isBundle={false}
          />
        </Content>
      </Body>
    </Dialog>
  );
};
